import { useState } from 'react';
import { API_BASE } from '../config';

function timestamp() {
  const d = new Date();
  return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}`;
}

export function useCommand() {
  const [consoleLines, setConsoleLines] = useState([
    { text: `[${timestamp()}] LWP v1.0.0 console/init`, type: 'system' }
  ]);

  const sendCommand = async (cmd, channelName = 'dm-tools') => {
    if (!cmd) return;
    if (cmd === '!clear' || cmd === 'clear') { setConsoleLines([{ text: `[${timestamp()}] Console cleared.`, type: 'system' }]); window._logCleared = true; return; }
    const session = localStorage.getItem('session');
    if (!session) {
      setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ❌ Not logged in.`, type: 'error' }]);
      return;
    }

    // Standalone mode: route combat commands to combat engine Lambda
    const roomId = localStorage.getItem('active_room');
    if (roomId) {
      try {
        let endpoint = null;
        let body = { room_id: roomId };

        if (cmd === '!f' || cmd.startsWith('!fight')) {
          endpoint = '/combat/start';
          body.enemy = 'Slappy'; body.count = 1;
          window._lootDismissed = false;
        } else if (cmd.startsWith('!sp ')) {
          endpoint = '/combat/start';
          window._lootDismissed = false;
          const parts = cmd.slice(4).trim().split(/\s+/);
          const countMatch = parts[parts.length - 1].match(/^x?(\d+)$/i);
          if (countMatch) { body.count = parseInt(countMatch[1]); body.enemy = parts.slice(0, -1).join(' ') || 'Slappy'; }
          else { body.enemy = parts.join(' ') || 'Slappy'; body.count = 1; }
        } else if (cmd === '!target' || cmd.startsWith('!target ')) {
          endpoint = '/combat/action';
          body.action = 'attack';
          body.target = cmd.split(' ').slice(1).join(' ') || null;
        } else if (cmd.startsWith('!cast ')) {
          endpoint = '/combat/action';
          body.action = 'cast';
          const parts = cmd.slice(6).trim().split(' on ');
          body.spell = parts[0]; body.target = parts[1] || null;
        } else if (cmd === '!skip') {
          endpoint = '/combat/action';
          body.action = 'skip';
        } else if (cmd === '!q' || cmd === '!endcombat') {
          endpoint = '/combat/end';
        } else if (cmd.startsWith('!dmheal ')) {
          endpoint = '/combat/action';
          body.action = 'heal';
          const parts = cmd.slice(8).trim().split(/\s+/);
          body.target = parts[0]; body.amount = parseInt(parts[1]) || 999;
        } else if (cmd.startsWith('!dmhit ')) {
          endpoint = '/combat/action';
          body.action = 'damage';
          const parts = cmd.slice(7).trim().split(/\s+/);
          body.target = parts[0]; body.amount = parseInt(parts[1]) || 10;
        } else if (cmd.startsWith('!inflict ')) {
          endpoint = '/combat/action';
          body.action = 'inflict';
          const parts = cmd.slice(9).trim().split(/\s+/);
          body.target = parts[0]; body.condition = parts.slice(1).join(' ') || 'poisoned';
        } else if (cmd.startsWith('!removecondition ')) {
          endpoint = '/combat/action';
          body.action = 'removecondition';
          const parts = cmd.slice(17).trim().split(/\s+/);
          body.target = parts[0]; body.condition = parts.slice(1).join(' ');
        } else if (cmd.startsWith('!give ')) {
          endpoint = '/combat/action';
          body.action = 'give';
          const parts = cmd.slice(6).trim().split(/\s+/);
          body.target = parts[0]; body.item = parts.slice(1).join(' ');
        } else if (cmd === '!lr' || cmd === '!longrest') {
          endpoint = '/combat/action';
          body.action = 'longrest';
        } else if (cmd === '!sr' || cmd === '!shortrest') {
          endpoint = '/combat/action';
          body.action = 'shortrest';
        } else if (cmd.startsWith('!godmode')) {
          endpoint = '/combat/action';
          body.action = 'godmode';
          body.target = cmd.slice(9).trim() || null;
        } else if (cmd.startsWith('!equip ')) {
          endpoint = '/combat/action';
          body.action = 'equip';
          const eqStr = cmd.slice(7).trim();
          const fromMatch = eqStr.match(/^(.+?) from (.+?) on (.+)$/i);
          const onMatch = eqStr.match(/^(.+?) on (.+)$/i);
          if (fromMatch) {
            body.item = fromMatch[1]; body.source = fromMatch[2]; body.target = fromMatch[3];
          } else if (onMatch) {
            body.item = onMatch[1]; body.target = onMatch[2];
          } else {
            body.item = eqStr;
          }
        } else if (cmd.startsWith('!unequip ')) {
          endpoint = '/combat/action';
          body.action = 'unequip';
          body.slot = cmd.slice(9).trim();
        }

        if (endpoint) {
          // Guardrail: prevent spam clicking (1s cooldown)
          if (window._combatCooldown) return;
          window._combatCooldown = true;
          setTimeout(() => { window._combatCooldown = false; }, 1000);

          if (window._turnTimer) { clearTimeout(window._turnTimer); window._turnTimer = null; }
          const resp = await fetch(`${API_BASE}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session },
            body: JSON.stringify(body)
          });
          const data = await resp.json();
          if (resp.ok) {
            setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} ✓`, type: 'success' }]);
            // Show player action immediately, delay enemy lines
            if (data.log) {
              const playerLines = data.log.filter(l => !l.startsWith('👹') && !l.includes('Enemy Phase'));
              const enemyLines = data.log.filter(l => l.startsWith('👹') || l.includes('Enemy Phase'));
              // Player lines immediately
              playerLines.forEach(l => setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${l}`, type: 'info' }]));
              // Enemy lines with dramatic delay
              if (enemyLines.length > 0) {
                setConsoleLines(prev => [...prev, { text: `[${timestamp()}] 🎲 . . .`, type: 'system', className: 'dice-rolling' }]);
                setTimeout(() => {
                  setConsoleLines(prev => {
                    // Remove the dice rolling line
                    const filtered = prev.filter(l => !l.text.includes('🎲 . . .'));
                    const newLines = enemyLines.map(l => ({ text: `[${timestamp()}] ${l}`, type: 'info' }));
                    return [...filtered, ...newLines];
                  });
                }, 800);
              }
            }
            if (data.current_turn) {
              const delay = data.log && data.log.some(l => l.startsWith('👹') || l.includes('Enemy Phase')) ? 900 : 0;
              setTimeout(() => {
                setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ⚔️ ${data.current_turn}'s turn — 60s to act`, type: 'system' }]);
              }, delay);
              // Start 60s auto-skip timer
              if (window._turnTimer) clearTimeout(window._turnTimer);
              window._turnTimer = setTimeout(() => {
                setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ⏰ Time's up! Auto-skipping...`, type: 'system' }]);
                sendCommand('!skip');
              }, 60000);
            }
            if (data.combat_ended) {
              if (window._turnTimer) { clearTimeout(window._turnTimer); window._turnTimer = null; }
              setConsoleLines(prev => [...prev, { text: `[${timestamp()}] 🏳️ Combat ended. XP: ${data.xp_awarded || 0} each.`, type: 'system' }]);
              if (data.level_ups && data.level_ups.length > 0) {
                data.level_ups.forEach(l => setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${l}`, type: 'system' }]));
              }
              if (data.loot && (data.loot.gear.length > 0 || data.loot.consumables.length > 0 || data.loot.gold > 0)) {
                // Display loot in console
                setConsoleLines(prev => {
                  const lines = [...prev, { text: `[${timestamp()}] ━━━━━ 💎 LOOT DROPPED ━━━━━`, type: 'system' }];
                  for (const g of data.loot.gear) {
                    const winner = g.rolls && g.rolls[0] ? `→ ${g.winner} 🏆 (${g.rolls[0].type} ${g.rolls[0].roll})` : '';
                    lines.push({ text: `[${timestamp()}] ⚔️ ${g.item} ${winner}`, type: 'info' });
                  }
                  if (data.loot.gold > 0) {
                    lines.push({ text: `[${timestamp()}] 💰 ${data.loot.gold} gold (${data.loot.gold_per_player} each)`, type: 'info' });
                  }
                  for (const c of data.loot.consumables) {
                    lines.push({ text: `[${timestamp()}] 🧪 ${c}`, type: 'info' });
                  }
                  lines.push({ text: `[${timestamp()}] ━━━━━━━━━━━━━━━━━━━━━━━━`, type: 'system' });
                  return lines;
                });
              }
            }
          } else {
            setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} → ❌ ${data.error || resp.status}`, type: 'error' }]);
          }
          return;
        }
      } catch (e) {
        setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} → ❌ Network error`, type: 'error' }]);
        return;
      }
    }

    // Discord mode: send via SQS command relay
    try {
      const resp = await fetch(`${API_BASE}/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session },
        body: JSON.stringify({ command: cmd, guild_id: null, channel_name: channelName })
      });
      if (resp.ok) {
        setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} → #${channelName} ✓`, type: 'success' }]);
      } else {
        setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} → ❌ ${resp.status}`, type: 'error' }]);
      }
    } catch (e) {
      setConsoleLines(prev => [...prev, { text: `[${timestamp()}] ${cmd} → ❌ Network error`, type: 'error' }]);
    }
  };

  return { consoleLines, sendCommand, setConsoleLines };
}
